import { useHistory } from "react-router-dom";
import { cn } from "../lib/utils";
import JuisciLogo from "../assets/core/juisci-logo-circle.svg";
import CloseButtonGrey from "../assets/icons/close-button-grey.svg?react";
import { useCurrentUser } from "../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../domains/app/hooks/useActiveLang";
import { getUserStartingPage } from "../domains/user/utils/getUserStartingPage";
import {
  gaEventNotificationClicked,
  gaEventNotificationOpened,
} from "../tools/analytics/domains/notification.analytics";
import { openExternalUrl } from "../tools/open-external-url";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { NotificationType } from "../../../api/src/shared/domain/notification";
import type { Api } from "../services/api";

type FormattedNotification =
  | Api["get"]["/webapp/private/notification/broadcasts"]["output"]["body"][number]
  | Api["get"]["/webapp/private/notification/personals"]["output"]["body"][number];

export default function NotificationItem({
  notification,
  onDelete,
  isRemoving = false,
}: {
  notification: FormattedNotification;
  onDelete: () => void;
  isRemoving?: boolean;
}) {
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const WEBAPP_JOURNAL_LOGO = useFeatureIsOn("WEBAPP_JOURNAL_LOGO");
  const content = notification.contents[activeLang] ?? notification.contents.en;
  const { title, text } = content;

  const journal =
    notification.type === NotificationType.ArticlePublished
      ? notification.journal
      : null;
  const avatar =
    journal?.image !== undefined &&
    journal.image !== null &&
    journal.image !== "" &&
    WEBAPP_JOURNAL_LOGO
      ? journal.image
      : JuisciLogo;

  let internalLink = "";
  let externalLink = "";

  switch (notification.type) {
    case NotificationType.LemonCertifiedAnswered: {
      if (!notification.read) return null;
      internalLink = "/settings";
      break;
    }
    case NotificationType.ContentNew:
    case NotificationType.ContentRecommended: {
      // Legacy types - hardcoded to home
      internalLink = "/";
      break;
    }
    case NotificationType.ArticlePublished: {
      internalLink = `/post/${notification.article.slug}`;
      break;
    }
    case NotificationType.VideoPublished: {
      const { slug, videoFormat } = notification.content;
      internalLink =
        videoFormat === "story" ? `/video/story/${slug}` : `/video/${slug}`;
      break;
    }
    case NotificationType.Broadcast:
    case NotificationType.ExternalLink: {
      // Unified Broadcast type and legacy ExternalLink both have link
      const { link, room } = notification;
      if (link === "/") {
        // "/" means personalized home - use notification's room if present
        internalLink = room === null ? "/discovery" : `/room/discovery/${room}`;
      } else if (link.includes("juisci.com")) {
        internalLink = link.split(".com").at(1) ?? "";
      } else {
        externalLink = link;
      }
      break;
    }
    case NotificationType.TestNotification: {
      break;
    }
    case NotificationType.PlaylistShared: {
      internalLink = `/profile/playlist/${notification.playlist.id}`;
      break;
    }
    case NotificationType.VisualAbstractReady: {
      internalLink = `/visual-abstract/${notification.visualAbstract.id}/preview`;
      break;
    }
    case NotificationType.Workflow: {
      // A workflow resolved its own destination at send time and stored it on the
      // contents, per language — the same slot `title`/`text` come from above. No
      // link means the workflow sent none: the row stays readable, just inert.
      const link = content.link;
      if (link === null) break;
      if (link.includes("juisci.com")) {
        const path = link.split(".com").at(1);
        internalLink = path === undefined ? "" : path;
      } else if (link.startsWith("/")) {
        internalLink = link;
      } else {
        externalLink = link;
      }
      break;
    }
    default: {
      throw new Error(notification satisfies never);
    }
  }

  async function handleNavigateInternal(link: string) {
    if (link === "/" && currentUser) {
      link = await getUserStartingPage(currentUser);
    }
    history.push(link);
  }

  async function trackClickedEvent() {
    if (currentUser) {
      gaEventNotificationClicked(notification.id, currentUser.uid);
    }
  }

  async function trackOpenedEvent() {
    if (currentUser) {
      gaEventNotificationOpened(notification.id, currentUser.uid);
    }
  }

  return (
    <>
      <div
        className={cn(
          "py-4 flex items-start gap-4 transition-[opacity,transform] duration-300 ease-out",
          isRemoving ? "opacity-0 -translate-x-5" : "opacity-100 translate-x-0"
        )}
        onClick={() => {
          trackOpenedEvent();
          if (internalLink) handleNavigateInternal(internalLink);
          else if (externalLink) openExternalUrl(externalLink);
        }}
      >
        <img
          className="object-contain size-10  rounded-full border-2 border-white shadow-card-subtle bg-[lightgray]"
          src={avatar}
          alt=""
        />
        <div>
          <span className="block text-primary-black font-dm-sans text-sm/normal font-semibold ">
            {title}
          </span>
          <span className="block mt-2 text-secondary-bluegrey-6 font-dm-sans text-[13px] font-light leading-[130%]">
            {text}
          </span>
        </div>
        <div className="ms-auto self-center flex items-center gap-3 [&_svg]:size-8 ">
          <CloseButtonGrey
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              trackClickedEvent();
            }}
          />
        </div>
      </div>
      <hr />
    </>
  );
}
